import { CustomField } from '../../../types/custom-fields/types';
import { buildFilterOptionsFromCustomFields } from '../../../utils/buildFilterOptionsFromCustomFields';
import { stateOptions } from '../../../utils/stateOptions';

type Option = { label: string; value: string };

interface Market {
  id: string;
  name: string;
}

interface StateData {
  name: string;
  markets: Market[];
}

// Hardcoded fallback options for practice filters
// These are used when custom fields don't exist in API response
const PRACTICE_FILTER_FALLBACKS: Record<string, Option[]> = {
  'Takes Walkins': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'New Telehealth': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'Flu Shots': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
    { label: 'Limited', value: 'Limited' },
  ],
  'New Patient': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
    { label: 'Priority', value: 'Priority' },
  ],
  'Other Languages': [
    { label: 'English', value: 'English' },
    { label: 'Hindi', value: 'Hindi' },
    { label: 'Spanish', value: 'Spanish' },
    { label: 'Urdu', value: 'Urdu' },
    { label: 'Korean', value: 'Korean' },
    { label: 'Arabic', value: 'Arabic' },
  ].sort((a, b) => a.label.localeCompare(b.label)),
  PAP: [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'Chronic Pain Management': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'ADD/ADHD': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'Unvaccinated Children': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'OB Visits': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'Workers Comp': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
    { label: 'Appointment Request', value: 'Appointment Request' },
    { label: 'VWR', value: 'VWR' },
  ],
  'Disability Physical': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'Military Physical': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'Flight Physical': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'DOT Physical': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  'Immigration Physical': [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
};

export const filterOptions = (
  statesData: StateData[],
  specialtyOptions: Option[] = [],
  customFields: CustomField[] | undefined | null = null
) => {
  // Build filter options dynamically from custom fields
  const dynamicOptions = buildFilterOptionsFromCustomFields(
    customFields,
    statesData,
    specialtyOptions,
    false, // Don't include States here - we'll add it separately
    true // isPractice = true
  );

  // Merge with hardcoded fallbacks
  // Dynamic options take precedence, but fallbacks fill in missing filters
  const mergedOptions: Record<string, Option[]> = {
    ...PRACTICE_FILTER_FALLBACKS,
    ...dynamicOptions,
  };

  // Build Market and States options
  const marketOptions = statesData
    .flatMap((state) =>
      state.markets.map((market) => ({
        label: market.name,
        value: market.id,
      }))
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  // Remove Market, States, and Specialty from mergedOptions if they exist (we'll add them at the top/in correct position)
  const restOfOptions = Object.fromEntries(
    Object.entries(mergedOptions).filter(
      ([key]) => key !== 'Market' && key !== 'States' && key !== 'Specialty'
    )
  );

  // Return object with Market and States at the top
  // Object property order is preserved in JavaScript, so these will appear first
  const orderedOptions: Record<string, Option[]> = {
    Market: marketOptions,
    States: stateOptions,
    ...restOfOptions,
  };

  // Always set Specialty if options are provided
  // Only include Specialty filter when it has options (don't show empty dropdown)
  if (specialtyOptions && specialtyOptions.length > 0) {
    orderedOptions.Specialty = specialtyOptions;
  }

  return orderedOptions;
};
